import React, { useState, useEffect } from 'react';
import CountryFlag from '../../../shared/components/CountryFlag';
import {
    Table,
    Button,
    Space,
    Tag,
    Modal,
    Form,
    Input,
    Select,
    message,
    Card,
    Drawer,
    Popconfirm,
    Tooltip,
    InputNumber,
    Descriptions,
    Badge,
    Tabs,
    List,
    Collapse,
    Typography
} from 'antd';
import {
    PlusOutlined,
    DeleteOutlined,
    EditOutlined,
    ApartmentOutlined,
    SyncOutlined,
    CloudServerOutlined,
    SubnodeOutlined,
    NodeIndexOutlined,
    LinkOutlined
} from '@ant-design/icons';
import evmAdminService from '../../../shared/services/evmAdminService';

const { Option } = Select;
const { Panel } = Collapse;
const { Text } = Typography;

const VpcManagement = () => {
    const [vpcs, setVpcs] = useState([]);
    const [areas, setAreas] = useState([]);
    const [zones, setZones] = useState([]);
    const [loading, setLoading] = useState(false);
    const [createDrawerVisible, setCreateDrawerVisible] = useState(false);
    const [editDrawerVisible, setEditDrawerVisible] = useState(false);
    const [detailDrawerVisible, setDetailDrawerVisible] = useState(false);
    const [subnetModalVisible, setSubnetModalVisible] = useState(false);
    const [nodeModalVisible, setNodeModalVisible] = useState(false);
    const [currentVpc, setCurrentVpc] = useState(null);
    const [vpcNodes, setVpcNodes] = useState([]);
    const [availableNodes, setAvailableNodes] = useState([]);

    const [createForm] = Form.useForm();
    const [editForm] = Form.useForm();
    const [subnetForm] = Form.useForm();

    useEffect(() => {
        loadVpcs();
        loadAreas();
    }, []);

    const loadVpcs = async () => {
        setLoading(true);
        try {
            const response = await evmAdminService.getVpcList();
            if (response.status === 'success') {
                setVpcs(response.data?.data || []);
            } else {
                message.error(response.msg || 'Failed to load VPCs');
            }
        } catch (error) {
            message.error('Failed to load VPCs');
        } finally {
            setLoading(false);
        }
    };

    const loadAreas = async () => {
        try {
            const response = await evmAdminService.getAreas();
            if (response.status === 'success') {
                const areasData = Array.isArray(response.data)
                    ? response.data
                    : (response.data?.data || response.data?.areas || []);
                setAreas(areasData);
            }
        } catch (error) {
            console.error('Failed to load areas', error);
        }
    };

    const loadZonesByArea = async (areaId) => {
        try {
            const response = await evmAdminService.getZonesByArea(areaId);
            if (response.status === 'success') {
                const zonesData = Array.isArray(response.data)
                    ? response.data
                    : (response.data?.data || response.data?.zones || []);
                setZones(zonesData);
            }
        } catch (error) {
            console.error('Failed to load zones', error);
        }
    };

    const loadVpcNodes = async (vpcId) => {
        try {
            const response = await evmAdminService.getVpcNodes(vpcId);
            if (response.status === 'success') {
                const nodesData = Array.isArray(response.data)
                    ? response.data
                    : (response.data?.data || response.data?.nodes || []);
                setVpcNodes(nodesData);
            }
        } catch (error) {
            console.error('Failed to load VPC nodes', error);
        }
    };

    const loadAvailableNodes = async (zoneId) => {
        try {
            const response = await evmAdminService.getZoneAvailableNodes(zoneId);
            if (response.status === 'success') {
                const nodesData = Array.isArray(response.data)
                    ? response.data
                    : (response.data?.data || response.data?.nodes || []);
                setAvailableNodes(nodesData);
            }
        } catch (error) {
            console.error('Failed to load available nodes', error);
        }
    };

    const handleDelete = async (vpc) => {
        try {
            const response = await evmAdminService.deleteVpc(vpc.id);
            if (response.status === 'success') {
                message.success('VPC deleted');
                loadVpcs();
            } else {
                message.error(response.msg || 'Delete failed');
            }
        } catch (error) {
            message.error('Delete failed');
        }
    };

    const showCreateDrawer = () => {
        createForm.resetFields();
        setZones([]);
        setCreateDrawerVisible(true);
    };

    const showEditDrawer = (vpc) => {
        setCurrentVpc(vpc);
        editForm.setFieldsValue(vpc);
        setEditDrawerVisible(true);
    };

    const showDetailDrawer = async (vpc) => {
        setCurrentVpc(vpc);
        await loadVpcNodes(vpc.id);
        setDetailDrawerVisible(true);
    };

    const showSubnetModal = (vpc) => {
        setCurrentVpc(vpc);
        subnetForm.resetFields();
        setSubnetModalVisible(true);
    };

    const showNodeModal = async (vpc) => {
        setCurrentVpc(vpc);
        await loadVpcNodes(vpc.id);
        if (vpc.zone_id) {
            await loadAvailableNodes(vpc.zone_id);
        }
        setNodeModalVisible(true);
    };

    const handleCreate = async (values) => {
        try {
            const response = await evmAdminService.createVpc(values);
            if (response.status === 'success') {
                message.success('VPC created');
                setCreateDrawerVisible(false);
                loadVpcs();
            } else {
                message.error(response.msg || 'Failed to create VPC');
            }
        } catch (error) {
            message.error('Failed to create VPC');
        }
    };

    const handleUpdate = async (values) => {
        try {
            const response = await evmAdminService.updateVpc(currentVpc.id, values);
            if (response.status === 'success') {
                message.success('VPC updated');
                setEditDrawerVisible(false);
                loadVpcs();
            } else {
                message.error(response.msg || 'Failed to update VPC');
            }
        } catch (error) {
            message.error('Failed to update VPC');
        }
    };

    const handleAddSubnet = async (values) => {
        try {
            const response = await evmAdminService.addVpcSubnet(currentVpc.id, values);
            if (response.status === 'success') {
                message.success('Subnet added');
                setSubnetModalVisible(false);
                loadVpcs();
            } else {
                message.error(response.msg || 'Failed to add subnet');
            }
        } catch (error) {
            message.error('Failed to add subnet');
        }
    };

    const handleDeleteSubnet = async (vpc, subnetId) => {
        try {
            const response = await evmAdminService.deleteVpcSubnet(vpc.id, subnetId);
            if (response.status === 'success') {
                message.success('Subnet deleted');
                loadVpcs();
            } else {
                message.error(response.msg || 'Failed to delete subnet');
            }
        } catch (error) {
            message.error('Failed to delete subnet');
        }
    };

    const handleAddNode = async (nodeId) => {
        try {
            const response = await evmAdminService.addVpcNode(currentVpc.id, nodeId);
            if (response.status === 'success') {
                message.success('Node added to VPC');
                await loadVpcNodes(currentVpc.id);
            } else {
                message.error(response.msg || 'Failed to add node');
            }
        } catch (error) {
            message.error('Failed to add node');
        }
    };

    const handleRemoveNode = async (nodeId) => {
        try {
            const response = await evmAdminService.removeVpcNode(currentVpc.id, nodeId);
            if (response.status === 'success') {
                message.success('Node removed from VPC');
                await loadVpcNodes(currentVpc.id);
            } else {
                message.error(response.msg || 'Failed to remove node');
            }
        } catch (error) {
            message.error('Failed to remove node');
        }
    };

    const getStatusTag = (status) => {
        const statusMap = {
            'active': { color: 'green', text: 'Active' },
            'inactive': { color: 'red', text: 'Inactive' },
            'deleted': { color: 'gray', text: 'Deleted' }
        };
        const config = statusMap[status] || { color: 'default', text: status };
        return <Tag color={config.color}>{config.text}</Tag>;
    };

    const columns = [
        {
            title: 'ID',
            dataIndex: 'id',
            key: 'id',
            width: 60
        },
        {
            title: 'Name',
            dataIndex: 'name',
            key: 'name',
            width: 150,
            render: (text, record) => (
                <a onClick={() => showDetailDrawer(record)}>{text}</a>
            )
        },
        {
            title: 'VNI',
            dataIndex: 'vni',
            key: 'vni',
            width: 80
        },
        {
            title: 'CIDR',
            dataIndex: 'cidr',
            key: 'cidr',
            width: 140
        },
        {
            title: 'Zone',
            key: 'zone',
            width: 150,
            render: (_, record) => <><CountryFlag code={areas.find((a) => Number(a.id) === Number(record.area_id ?? record.zone?.area_id))?.country} />{record.zone?.name || `Zone ${record.zone_id}`}</>
        },
        {
            title: 'Subnets',
            dataIndex: 'subnets',
            key: 'subnets',
            width: 80,
            render: (subnets) => subnets?.length || 0
        },
        {
            title: 'User ID',
            dataIndex: 'user_id',
            key: 'user_id',
            width: 80
        },
        {
            title: 'Status',
            dataIndex: 'status',
            key: 'status',
            width: 100,
            render: (status) => getStatusTag(status)
        },
        {
            title: 'Actions',
            key: 'action',
            fixed: 'right',
            width: 250,
            render: (_, record) => (
                <Space size="small">
                    <Tooltip title="Manage Nodes">
                        <Button
                            size="small"
                            icon={<NodeIndexOutlined />}
                            onClick={() => showNodeModal(record)}
                        />
                    </Tooltip>
                    <Tooltip title="添加子网">
                        <Button
                            size="small"
                            icon={<SubnodeOutlined />}
                            onClick={() => showSubnetModal(record)}
                        />
                    </Tooltip>
                    <Tooltip title="Edit">
                        <Button
                            size="small"
                            icon={<EditOutlined />}
                            onClick={() => showEditDrawer(record)}
                        />
                    </Tooltip>
                    <Popconfirm
                        title="Delete this VPC?"
                        description="Please delete all subnets first."
                        onConfirm={() => handleDelete(record)}
                    >
                        <Tooltip title="Delete">
                            <Button size="small" danger icon={<DeleteOutlined />} />
                        </Tooltip>
                    </Popconfirm>
                </Space>
            )
        }
    ];

    const expandedRowRender = (vpc) => {
        if (!vpc.subnets || vpc.subnets.length === 0) {
            return <Text type="secondary">No subnets</Text>;
        }

        const subnetColumns = [
            { title: 'ID', dataIndex: 'id', key: 'id', width: 60 },
            { title: 'Name', dataIndex: 'name', key: 'name', width: 150 },
            { title: 'CIDR', dataIndex: 'cidr', key: 'cidr', width: 140 },
            { title: 'Gateway', dataIndex: 'gateway', key: 'gateway', width: 130 },
            { title: 'DNS', key: 'dns', width: 200, render: (_, r) => `${r.dns1}, ${r.dns2}` },
            { title: 'Status', dataIndex: 'status', key: 'status', width: 80 },
            {
                title: 'Action',
                key: 'action',
                width: 80,
                render: (_, subnet) => (
                    <Popconfirm
                        title="Delete this subnet?"
                        onConfirm={() => handleDeleteSubnet(vpc, subnet.id)}
                    >
                        <Button size="small" danger icon={<DeleteOutlined />} />
                    </Popconfirm>
                )
            }
        ];

        return (
            <Table
                columns={subnetColumns}
                dataSource={vpc.subnets}
                rowKey="id"
                pagination={false}
                size="small"
            />
        );
    };

    return (
        <div>
            <Card
                title={<span><ApartmentOutlined /> VPC管理</span>}
                extra={
                    <Space>
                        <Button type="primary" icon={<PlusOutlined />} onClick={showCreateDrawer}>
                            Create VPC
                        </Button>
                        <Button icon={<SyncOutlined />} onClick={loadVpcs}>
                            Refresh
                        </Button>
                    </Space>
                }
            >
                <Table
                    columns={columns}
                    dataSource={vpcs}
                    loading={loading}
                    rowKey="id"
                    scroll={{ x: 1200 }}
                    pagination={{ pageSize: 20 }}
                    expandable={{
                        expandedRowRender,
                        rowExpandable: (record) => record.subnets && record.subnets.length > 0
                    }}
                />
            </Card>

            {/* Create VPC Drawer */}
            <Drawer
                title="创建 VPC"
                width={500}
                open={createDrawerVisible}
                onClose={() => setCreateDrawerVisible(false)}
                extra={
                    <Space>
                        <Button onClick={() => setCreateDrawerVisible(false)}>Cancel</Button>
                        <Button type="primary" onClick={() => createForm.submit()}>Create</Button>
                    </Space>
                }
            >
                <Form form={createForm} layout="vertical" onFinish={handleCreate}>
                    <Form.Item
                        name="name"
                        label="VPC Name"
                        rules={[{ required: true, message: 'Please enter VPC name' }]}
                    >
                        <Input placeholder="e.g., my-vpc" />
                    </Form.Item>

                    <Form.Item
                        name="area_id"
                        label="Area"
                        rules={[{ required: true, message: 'Please select area' }]}
                    >
                        <Select
                            placeholder="Select area"
                            onChange={(value) => loadZonesByArea(value)}
                        >
                            {areas.map(area => (
                                <Option key={area.id} value={area.id}><CountryFlag code={area.country} />{area.name}</Option>
                            ))}
                        </Select>
                    </Form.Item>

                    <Form.Item
                        name="zone_id"
                        label="Zone"
                        rules={[{ required: true, message: 'Please select zone' }]}
                    >
                        <Select placeholder="Select zone">
                            {zones.map(zone => (
                                <Option key={zone.id} value={zone.id}>{zone.name} ({zone.code})</Option>
                            ))}
                        </Select>
                    </Form.Item>

                    <Form.Item
                        name="vni"
                        label="VNI (VXLAN Network Identifier)"
                        rules={[{ required: true, message: 'Please enter VNI' }]}
                        tooltip="Unique within the zone (1-16777215)"
                    >
                        <InputNumber min={1} max={16777215} style={{ width: '100%' }} />
                    </Form.Item>

                    <Form.Item
                        name="cidr"
                        label="CIDR"
                        rules={[{ required: true, message: 'Please enter CIDR' }]}
                        tooltip="e.g., 10.0.0.0/16"
                    >
                        <Input placeholder="e.g., 10.0.0.0/16" />
                    </Form.Item>

                    <Form.Item name="user_id" label="User ID (0 for admin)" initialValue={0}>
                        <InputNumber min={0} style={{ width: '100%' }} />
                    </Form.Item>
                </Form>
            </Drawer>

            {/* Edit VPC Drawer */}
            <Drawer
                title="编辑 VPC"
                width={400}
                open={editDrawerVisible}
                onClose={() => setEditDrawerVisible(false)}
                extra={
                    <Space>
                        <Button onClick={() => setEditDrawerVisible(false)}>Cancel</Button>
                        <Button type="primary" onClick={() => editForm.submit()}>Save</Button>
                    </Space>
                }
            >
                <Form form={editForm} layout="vertical" onFinish={handleUpdate}>
                    <Form.Item name="name" label="VPC Name">
                        <Input />
                    </Form.Item>
                    <Form.Item name="status" label="Status">
                        <Select>
                            <Option value="active">Active</Option>
                            <Option value="inactive">Inactive</Option>
                        </Select>
                    </Form.Item>
                </Form>
            </Drawer>

            {/* VPC Detail Drawer */}
            <Drawer
                title={`VPC: ${currentVpc?.name || ''}`}
                width={600}
                open={detailDrawerVisible}
                onClose={() => setDetailDrawerVisible(false)}
            >
                {currentVpc && (
                    <div>
                        <Descriptions bordered column={1} size="small">
                            <Descriptions.Item label="ID">{currentVpc.id}</Descriptions.Item>
                            <Descriptions.Item label="Name">{currentVpc.name}</Descriptions.Item>
                            <Descriptions.Item label="VNI">{currentVpc.vni}</Descriptions.Item>
                            <Descriptions.Item label="CIDR">{currentVpc.cidr}</Descriptions.Item>
                            <Descriptions.Item label="Zone">{currentVpc.zone?.name}</Descriptions.Item>
                            <Descriptions.Item label="Status">{getStatusTag(currentVpc.status)}</Descriptions.Item>
                        </Descriptions>

                        <Collapse style={{ marginTop: 16 }} defaultActiveKey={['1', '2']}>
                            <Panel header={`Nodes (${vpcNodes.length})`} key="1">
                                <List
                                    dataSource={vpcNodes}
                                    locale={{ emptyText: 'No nodes assigned' }}
                                    renderItem={node => (
                                        <List.Item>
                                            <List.Item.Meta
                                                avatar={<CloudServerOutlined />}
                                                title={node.name}
                                                description={`IP: ${node.ipaddress} | VXLAN: ${node.vxlan_ip}`}
                                            />
                                            <Tag color={node.status === 'active' ? 'green' : 'orange'}>
                                                {node.status}
                                            </Tag>
                                        </List.Item>
                                    )}
                                />
                            </Panel>
                            <Panel header={`Subnets (${currentVpc.subnets?.length || 0})`} key="2">
                                <List
                                    dataSource={currentVpc.subnets || []}
                                    locale={{ emptyText: 'No subnets' }}
                                    renderItem={subnet => (
                                        <List.Item>
                                            <List.Item.Meta
                                                avatar={<SubnodeOutlined />}
                                                title={subnet.name}
                                                description={`CIDR: ${subnet.cidr} | Gateway: ${subnet.gateway}`}
                                            />
                                        </List.Item>
                                    )}
                                />
                            </Panel>
                        </Collapse>
                    </div>
                )}
            </Drawer>

            {/* Add Subnet Modal */}
            <Modal
                title="添加子网"
                open={subnetModalVisible}
                onCancel={() => setSubnetModalVisible(false)}
                onOk={() => subnetForm.submit()}
            >
                <Form form={subnetForm} layout="vertical" onFinish={handleAddSubnet}>
                    <Form.Item
                        name="name"
                        label="Subnet Name"
                        rules={[{ required: true, message: 'Please enter subnet name' }]}
                    >
                        <Input placeholder="e.g., private-subnet-1" />
                    </Form.Item>

                    <Form.Item
                        name="cidr"
                        label="CIDR"
                        rules={[{ required: true, message: 'Please enter CIDR' }]}
                    >
                        <Input placeholder="e.g., 10.0.1.0/24" />
                    </Form.Item>

                    <Form.Item
                        name="gateway"
                        label="Gateway"
                        rules={[{ required: true, message: 'Please enter gateway' }]}
                    >
                        <Input placeholder="e.g., 10.0.1.1" />
                    </Form.Item>

                    <Form.Item name="dns1" label="DNS 1" initialValue="8.8.8.8">
                        <Input />
                    </Form.Item>

                    <Form.Item name="dns2" label="DNS 2" initialValue="1.1.1.1">
                        <Input />
                    </Form.Item>

                    <Form.Item name="dhcp_start" label="DHCP Start">
                        <Input placeholder="e.g., 10.0.1.100" />
                    </Form.Item>

                    <Form.Item name="dhcp_end" label="DHCP End">
                        <Input placeholder="e.g., 10.0.1.200" />
                    </Form.Item>
                </Form>
            </Modal>

            {/* Manage Nodes Modal */}
            <Modal
                title={`Manage Nodes for VPC: ${currentVpc?.name || ''}`}
                open={nodeModalVisible}
                onCancel={() => setNodeModalVisible(false)}
                footer={null}
                width={700}
            >
                <Tabs
                    items={[
                        {
                            key: '1',
                            label: `Current Nodes (${vpcNodes.length})`,
                            children: (
                                <List
                                    dataSource={vpcNodes}
                                    locale={{ emptyText: 'No nodes in this VPC' }}
                                    renderItem={node => (
                                        <List.Item
                                            actions={[
                                                <Popconfirm
                                                    title="Remove this node from VPC?"
                                                    onConfirm={() => handleRemoveNode(node.node_id)}
                                                >
                                                    <Button size="small" danger>Remove</Button>
                                                </Popconfirm>
                                            ]}
                                        >
                                            <List.Item.Meta
                                                avatar={<CloudServerOutlined style={{ fontSize: 24 }} />}
                                                title={node.name}
                                                description={`IP: ${node.ipaddress} | VXLAN: ${node.vxlan_ip || 'N/A'}`}
                                            />
                                            <Tag color={node.status === 'active' ? 'green' : 'orange'}>{node.status}</Tag>
                                        </List.Item>
                                    )}
                                />
                            )
                        },
                        {
                            key: '2',
                            label: 'Add Nodes',
                            children: (
                                <List
                                    dataSource={availableNodes.filter(
                                        n => !vpcNodes.some(vn => vn.node_id === n.id)
                                    )}
                                    locale={{ emptyText: 'No available nodes in this zone' }}
                                    renderItem={node => (
                                        <List.Item
                                            actions={[
                                                <Button
                                                    type="primary"
                                                    size="small"
                                                    onClick={() => handleAddNode(node.id)}
                                                >
                                                    Add
                                                </Button>
                                            ]}
                                        >
                                            <List.Item.Meta
                                                avatar={<CloudServerOutlined style={{ fontSize: 24 }} />}
                                                title={node.name}
                                                description={`IP: ${node.ipaddress}`}
                                            />
                                            <Tag color={node.status === 'enable' ? 'green' : 'red'}>{node.status}</Tag>
                                        </List.Item>
                                    )}
                                />
                            )
                        }
                    ]}
                />
            </Modal>
        </div>
    );
};

export default VpcManagement;
